import { FC, useState, useMemo } from "react";
import { Spin, Empty } from "antd";
import { Clock, Truck, ArrowRight, Users } from "lucide-react";
import { useGetServiceTickets } from "~/services/service-ticket/service-ticket.queries";
import { useGetStaffAvailabilities } from "~/services/staff/staff-availability.queries";
import { TServiceTicket } from "~/types";
import { formatFullAddress, capitalize } from "~/utils";
import { formatStaffName } from "~/utils/format-staff-name";
import dayjs from "dayjs";
import { StaffAvailability } from "~/utils/constants";

interface MasterStaffingProps {
  projectId: string;
}

interface StaffInfo {
  staffId: string;
  availability: StaffAvailability;
  note?: string;
  staffName?: string;
  isDriver?: boolean;
  isLead?: boolean;
  bookedHours?: number;
  availableHours?: number;
}

export const MasterStaffing: FC<MasterStaffingProps> = ({ projectId }) => {
  const [selectedService, setSelectedService] = useState<TServiceTicket | null>(null);

  // Fetch ALL services for the project (no date filter)
  const { data: servicesData, isLoading: servicesLoading } = useGetServiceTickets({
    pageSize: 500, // Maximum allowed by backend
    page: 1,
  });

  // Filter services by project (via jobTicket.project relationship)
  const projectServices = useMemo(() => {
    if (!servicesData?.items) return [];
    return servicesData.items
      .filter(service => service.jobTicket?.project?.id === projectId)
      .sort((a, b) => {
        const dateA = dayjs(a.serviceDatetime || a.startDatetime);
        const dateB = dayjs(b.serviceDatetime || b.startDatetime);
        return dateA.isBefore(dateB) ? -1 : 1;
      });
  }, [servicesData?.items, projectId]);

  // Get the selected service's date for staff availability
  const selectedDate = selectedService
    ? dayjs(selectedService.serviceDatetime || selectedService.startDatetime)
    : null;

  const startOfDay = selectedDate?.startOf('day').toISOString();
  const endOfDay = selectedDate?.endOf('day').toISOString();

  // Fetch staff availability for the selected date
  const { data: scheduleData, isLoading: scheduleLoading } = useGetStaffAvailabilities(
    {
      startDate: startOfDay,
      endDate: endOfDay,
      pageSize: 100,
    },
    !!selectedDate
  );

  // Process staff availability data
  const staffData = useMemo(() => {
    if (!selectedDate || !scheduleData?.items) {
      return {
        scheduled: [],
        available: [],
        unavailable: [],
      };
    }

    const staffInfo: StaffInfo[] = [];

    scheduleData.items.forEach(schedule => {
      if (!schedule || !schedule.date) return;

      const info: StaffInfo = {
        staffId: schedule.staffId,
        availability: schedule.availability || StaffAvailability.FULL_TIME,
        note: schedule.note,
        staffName: formatStaffName(schedule.staff),
        isDriver: schedule.staff?.driver || false,
        isLead: schedule.staff?.lead || false,
        bookedHours: schedule.bookedHours || 0,
        availableHours: schedule.availableHours || 8,
      };

      staffInfo.push(info);
    });

    // Split into categories
    const scheduled = staffInfo.filter(s =>
      (s.availability === StaffAvailability.FULL_TIME || s.availability === StaffAvailability.PART_TIME) &&
      (s.bookedHours || 0) > 0
    );

    const available = staffInfo.filter(s =>
      (s.availability === StaffAvailability.FULL_TIME || s.availability === StaffAvailability.PART_TIME) &&
      (s.bookedHours || 0) === 0
    );

    const unavailable = staffInfo.filter(s => s.availability === StaffAvailability.UNAVAILABLE);

    return { scheduled, available, unavailable };
  }, [selectedDate, scheduleData]);

  return (
    <div className="grid grid-cols-12 gap-6 h-[calc(100vh-200px)]">
      {/* Left side: Service List */}
      <div className="col-span-5 bg-white rounded-lg border shadow-sm overflow-hidden flex flex-col">
        <div className="p-4 border-b font-medium bg-gray-50">
          Upcoming Services ({projectServices.length})
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <Spin spinning={servicesLoading}>
            {projectServices.length === 0 ? (
              <Empty description="No upcoming services for this project" />
            ) : (
              <div className="space-y-3">
                {projectServices.map((service) => (
                  <div
                    key={service.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${
                      selectedService?.id === service.id
                        ? "border-blue-500 bg-blue-50 shadow-sm"
                        : "hover:bg-gray-50 hover:border-gray-300"
                    }`}
                    onClick={() => setSelectedService(service)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-medium text-gray-900">{service.serial}</span>
                      <div className="flex flex-col items-end text-gray-500 text-xs">
                        <div className="flex items-center space-x-1">
                          <Clock size={12} />
                          <span>
                            {(service.serviceDatetime || service.startDatetime)
                              ? new Date(service.serviceDatetime || service.startDatetime).toLocaleDateString('en-US', {
                                  weekday: 'short',
                                  month: 'short',
                                  day: 'numeric'
                                })
                              : "N/A"}
                          </span>
                        </div>
                        <span className="font-medium text-gray-700">
                          {service.startDatetime
                            ? new Date(service.startDatetime).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                              })
                            : "N/A"}
                        </span>
                        {service.duration && (
                          <span className="text-gray-400">
                            {service.duration}h
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-sm mb-2 font-medium text-gray-700">
                      {capitalize(service.serviceType.replace(/_/g, ' '))}
                    </div>
                    <div className="text-sm mb-2 text-gray-600">
                      {service.client?.companyName || service.jobTicket?.client?.companyName || "Unnamed Client"}
                    </div>
                    {service.vessel && (
                      <div className="flex items-center space-x-1 text-gray-600 text-sm mb-2">
                        <Truck size={14} />
                        <span>{service.vessel.name} - {service.vessel.vesselRef}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-1 text-gray-600 text-sm mb-2">
                      <ArrowRight size={14} />
                      <span className="truncate">
                        {formatFullAddress(service?.location) || "No address"}
                      </span>
                    </div>
                    {service?.staffs && service.staffs.length > 0 && (
                      <div className="mt-2 flex items-center space-x-2 flex-wrap gap-1">
                        <Users size={14} className="text-gray-400" />
                        {service.staffs.slice(0, 3).map((staff) => (
                          <div
                            key={staff.id}
                            className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full"
                          >
                            {formatStaffName(staff)}
                          </div>
                        ))}
                        {service.staffs.length > 3 && (
                          <div className="text-xs px-2 py-1 bg-gray-200 rounded-full text-gray-600">
                            +{service.staffs.length - 3}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Spin>
        </div>
      </div>

      {/* Right side: Staff Availability */}
      <div className="col-span-7">
        {!selectedService ? (
          <div className="bg-white rounded-lg border shadow-sm h-full flex items-center justify-center">
            <div className="text-center text-gray-500">
              <Users size={48} className="mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium mb-2">Select a Service</h3>
              <p className="text-sm">Click on a service to view staff availability</p>
            </div>
          </div>
        ) : (
          <StaffAvailabilitySidebar
            selectedDate={selectedDate}
            selectedService={selectedService}
            scheduled={staffData.scheduled}
            available={staffData.available}
            unavailable={staffData.unavailable}
            isLoading={scheduleLoading}
          />
        )}
      </div>
    </div>
  );
};

// Staff Availability Sidebar Component
interface StaffAvailabilitySidebarProps {
  selectedDate: dayjs.Dayjs | null;
  selectedService: TServiceTicket;
  scheduled: StaffInfo[];
  available: StaffInfo[];
  unavailable: StaffInfo[];
  isLoading: boolean;
}

const StaffAvailabilitySidebar: FC<StaffAvailabilitySidebarProps> = ({
  selectedDate,
  selectedService,
  scheduled,
  available,
  unavailable,
  isLoading,
}) => {
  const [activeTab, setActiveTab] = useState<'scheduled' | 'available' | 'unavailable'>('scheduled');

  const tabs = [
    { key: 'scheduled' as const, label: `Scheduled (${scheduled.length})`, data: scheduled },
    { key: 'available' as const, label: `Available (${available.length})`, data: available },
    { key: 'unavailable' as const, label: `Unavailable (${unavailable.length})`, data: unavailable },
  ];

  return (
    <div className="bg-white rounded-lg border shadow-sm h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b bg-gray-50">
        <h3 className="font-semibold text-lg mb-1">Staff Availability</h3>
        <p className="text-sm text-gray-600">
          {selectedService.serial} - {selectedDate?.format('dddd, MMMM D, YYYY')}
        </p>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-2 mt-3">
          <div className="text-center">
            <div className="text-xl font-bold text-blue-600">{scheduled.length}</div>
            <div className="text-xs text-gray-600">Scheduled</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-green-600">{available.length}</div>
            <div className="text-xs text-gray-600">Available</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-red-600">{unavailable.length}</div>
            <div className="text-xs text-gray-600">Unavailable</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex">
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'border-b-2 border-blue-500 text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <Spin spinning={isLoading}>
          {activeTab === 'scheduled' && (
            <StaffList
              staff={scheduled}
              emptyMessage="No scheduled staff"
              colorScheme="blue"
              showHours
            />
          )}
          {activeTab === 'available' && (
            <StaffList
              staff={available}
              emptyMessage="No available staff"
              colorScheme="green"
              showHours
            />
          )}
          {activeTab === 'unavailable' && (
            <StaffList
              staff={unavailable}
              emptyMessage="No unavailable staff"
              colorScheme="red"
            />
          )}
        </Spin>
      </div>
    </div>
  );
};

// Staff List Component
interface StaffListProps {
  staff: StaffInfo[];
  emptyMessage: string;
  colorScheme: 'blue' | 'green' | 'red';
  showHours?: boolean;
}

const StaffList: FC<StaffListProps> = ({ staff, emptyMessage, colorScheme, showHours = false }) => {
  const colors = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-900', badge: 'bg-blue-500' },
    green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-900', badge: 'bg-green-500' },
    red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-900', badge: 'bg-red-500' },
  };

  const scheme = colors[colorScheme];

  if (staff.length === 0) {
    return (
      <div className="text-center text-gray-500 italic py-8">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {staff.map((s, idx) => (
        <div key={idx} className={`${scheme.bg} border ${scheme.border} rounded-lg p-3`}>
          <div className="flex justify-between items-start mb-2">
            <div className={`font-medium ${scheme.text}`}>
              {s.staffName}
              {s.availability === StaffAvailability.PART_TIME && (
                <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                  Part-Time
                </span>
              )}
            </div>
            <div className="flex gap-1">
              {s.isDriver && (
                <span className="text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded">D</span>
              )}
              {s.isLead && (
                <span className="text-xs bg-purple-500 text-white px-1.5 py-0.5 rounded">L</span>
              )}
            </div>
          </div>
          {showHours && (
            <>
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">Available Hours:</span>
                <span className={`font-semibold ${scheme.text}`}>
                  {Number(s.availableHours || 8).toFixed(1)} hrs
                </span>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-gray-600">Booked:</span>
                <span className="text-gray-500">
                  {Number(s.bookedHours || 0).toFixed(1)} hrs
                </span>
              </div>
            </>
          )}
          {s.note && (
            <div className={`text-xs text-gray-600 italic mt-2 pt-2 border-t ${scheme.border}`}>
              {s.note}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
